import {
  FormControl,
  Autocomplete,
  TextField,
  Paper,
  PaperProps
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {useAppSelector} from '@/redux-store/store';
import {useMemo} from 'react';
import {Sport} from '@/types/common';
import {FocusEvent} from 'react';
import {selectSportsValues} from '@/redux-store/slices/sports';
import FormLabel from '@/components/ui/form-label';

const StyledPaper: React.FC<PaperProps> = (props) => {
  return <Paper {...props} style={{maxWidth: 300, margin: 'auto'}} />;
};

export type SportSelectInputProps = {
  name?: string;
  label?: string;
  value?: string | string[] | null;
  onChange?: (newSportId: string | string[]) => void;
  error?: string | string[] | undefined;
  touched?: boolean;
  onBlur?: (e: FocusEvent<HTMLElement>) => void;
  multiple?: boolean;
  optional?: boolean;
};

export const SportSelectInput = ({
  value = '',
  label,
  onChange = () => {
    //noop
  },
  error,
  touched,
  onBlur = () => {
    //noop
  },
  multiple = false,
  optional = false
}: SportSelectInputProps) => {
  const {t, i18n} = useTranslation();

  const sportsFromRedux: Sport[] = useAppSelector(selectSportsValues);

  const defaultLanguage = 'de';
  const selectedLanguage = i18n.language || defaultLanguage;

  const inputLabel = label ?? (multiple ? 'interests' : 'interest');

  const sortedSportsFromRedux = useMemo(() => {
    const sortedSports = [...sportsFromRedux];
    sortedSports.sort((a, b) => {
      const labelA = a.labels[selectedLanguage as keyof Sport['labels']];
      const labelB = b.labels[selectedLanguage as keyof Sport['labels']];
      if (!labelA || !labelB) {
        return 0;
      }
      return labelA.localeCompare(labelB);
    });

    return sortedSports;
  }, [sportsFromRedux, selectedLanguage]);

  const handleChange = (
    event: React.SyntheticEvent<Element, Event>,
    newValue: Sport | Sport[] | (Sport | Sport[])[] | null
  ) => {
    if (Array.isArray(newValue)) {
      const flatArray = newValue.flat();
      onChange(flatArray.map((sport: Sport) => sport._id));
    } else if (newValue) {
      onChange(newValue._id);
    } else {
      onChange('');
    }
  };

  return (
    <FormControl sx={{width: '100%'}} variant="outlined" fullWidth>
      <Autocomplete
        PaperComponent={StyledPaper}
        noOptionsText={t('no_options')}
        multiple={multiple}
        disableCloseOnSelect={multiple}
        options={sortedSportsFromRedux}
        getOptionLabel={(option) =>
          Array.isArray(option)
            ? option
                .map(
                  (o) =>
                    o.labels[i18n.language as keyof Sport['labels']] ||
                    o.labels[defaultLanguage as keyof Sport['labels']]
                )
                .join(', ')
            : option.labels[i18n.language as keyof Sport['labels']] ||
              option.labels[defaultLanguage as keyof Sport['labels']]
        }
        value={
          multiple
            ? sortedSportsFromRedux.filter((sport) =>
                (value as string[]).includes(sport._id)
              )
            : value
            ? sortedSportsFromRedux.find((sport) => sport._id === value)
            : null
        }
        onChange={handleChange}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            label={
              <FormLabel text={inputLabel} required={optional ? false : true} />
            }
            error={touched && Boolean(error)}
            helperText={touched && error}
            onBlur={onBlur}
            fullWidth
          />
        )}
      />
    </FormControl>
  );
};
